import { RefObject, useLayoutEffect, useRef, useState } from "react";

import Portal from "components/Portal";

type BulkActionsFloatingBarSpacerProps = {
  isVisible: boolean;
  floatingBlockRef: RefObject<HTMLDivElement | null>;
  virtualizedListContainerRef?: RefObject<HTMLElement | null>;
  onHeightChange?: (height: number) => void;
};

const BOTTOM_PADDING_SIZE = 24; // bottom padding in the floating bar wrapper, made via bottom position to prevent scrollbars from appearing

/**
 * This component renders a fake "tombstone" component into the virtualized list container, to not
 * cover the content with the floating bar.
 *
 * Position sticky did not work, with a low amount of items in the virtualized list.
 *
 * If you don't pass the virtualizedListContainerRef, the spacer will not be rendered.
 */
const BulkActionsFloatingBarSpacer = ({
  isVisible,
  floatingBlockRef,
  virtualizedListContainerRef,
  onHeightChange,
}: BulkActionsFloatingBarSpacerProps) => {
  const spacerRef = useRef<HTMLDivElement>(null);
  const [mountElement, setMountElement] = useState<HTMLElement | null>(null);

  useLayoutEffect(() => {
    if (!spacerRef.current) {
      return;
    }
    spacerRef.current.style.display = isVisible ? "block" : "none";
  }, [isVisible]);

  useLayoutEffect(() => {
    const element = virtualizedListContainerRef?.current ?? null;
    const animationFrameId = requestAnimationFrame(() => {
      setMountElement(element);
    });

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
    // Re-resolve when visibility changes so we pick up refs that were set after initial mount
  }, [virtualizedListContainerRef, isVisible]);

  useLayoutEffect(() => {
    if (!isVisible || (!spacerRef.current && !onHeightChange)) {
      return;
    }

    let animationFrameId: number | undefined;
    let observer: ResizeObserver | undefined;
    let attempts = 0;
    const maxAttempts = 60; // ~ 1 second

    const attachObserver = () => {
      const floatingElement = floatingBlockRef.current;

      if (!floatingElement) {
        if (attempts < maxAttempts) {
          attempts += 1;
          animationFrameId = requestAnimationFrame(attachObserver);
        }
        return;
      }

      observer = new ResizeObserver(([entry]) => {
        const { blockSize } = entry.borderBoxSize[0];
        const size = blockSize ? blockSize + BOTTOM_PADDING_SIZE : 0;
        onHeightChange?.(size);

        if (spacerRef.current) {
          spacerRef.current.style.height = `${size}px`;
        }
      });

      observer.observe(floatingElement);
    };

    attachObserver();

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      observer?.disconnect();
    };
    // mountElement is needed so the effect re-runs after Portal renders and spacerRef becomes available
  }, [isVisible, floatingBlockRef, onHeightChange, mountElement]);

  if (!mountElement) {
    return null;
  }

  return (
    <Portal customMountElement={mountElement}>
      <div ref={spacerRef} />
    </Portal>
  );
};
export default BulkActionsFloatingBarSpacer;
